import { AdditiveBlending, CircleGeometry, Color, Mesh, ShaderMaterial } from 'three';
import { Palette } from '../Palette.js';
import { GLSL_COMMON } from '../shaders/glslCommon.js';

/**
 * The glowing mandala at the centre of the world.
 *
 * Drawn entirely in the fragment shader on one disc: **one draw call, one
 * geometry, no textures**. Everything — the concentric bands of petals, the ring
 * lines, the spokes, the rotation — is a distance function evaluated in polar
 * coordinates.
 *
 * This is the decision the prototype exists to validate. The Unity build drew its
 * rings by rewriting `LineRenderer` vertices every frame (96 points per arc, per
 * ring, per frame). Expressing the same imagery analytically means the CPU does
 * nothing, the silhouette is resolution-independent and perfectly antialiased, and
 * the twelve wave shapes the requirements ask for (§5.2: petal, polygon, spiral,
 * concentric, inverted) become a few extra lines in the distance function rather
 * than a new vertex generator each.
 *
 * The layers rotate at rates that share no common multiple, so the figure never
 * returns to a pose the viewer has already seen.
 */

export interface MandalaCoreOptions {
  readonly radius?: number;
  readonly gain?: number;
}

const VERTEX = /* glsl */ `
varying vec2 vLocal;

void main() {
  vLocal = position.xy;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAGMENT = /* glsl */ `
${GLSL_COMMON}

uniform float uTime;
uniform float uRadius;
uniform float uGain;
uniform float uBreath;
uniform float uPulse;
uniform vec3  uCoreColor;
uniform vec3  uMidColor;
uniform vec3  uEdgeColor;

varying vec2 vLocal;

/** Soft-edged annulus mask. */
float bandMask(float r, float inner, float outer, float soft) {
  return smoothstep(inner, inner + soft, r) * (1.0 - smoothstep(outer - soft, outer, r));
}

/**
 * One petal of a symmetric band.
 *
 * \`folded\` is 0 on a petal's axis and wedge/2 in the gap between petals, so the
 * petal is built by shrinking an allowed angular width as the radius approaches
 * either edge of its band. The sine envelope is what gives the lens shape.
 */
float petal(float folded, float wedge, float radial, float sharp) {
  float across = folded / max(wedge * 0.5, 1e-4);
  float envelope = sin(clamp(radial, 0.0, 1.0) * PI);
  float shape = 1.0 - smoothstep(0.0, envelope * 0.92 + 0.04, across);
  return pow(max(shape, 0.0), sharp);
}

/** A band of \`symmetry\` petals between two radii, spinning at \`spin\` rad/s. */
float petalBand(float r, float angle, float symmetry, float inner, float outer, float spin, float sharp) {
  float wedge = TAU / symmetry;
  float folded = kaleido(angle + uTime * spin, symmetry);
  float radial = (r - inner) / max(outer - inner, 1e-4);
  return bandMask(r, inner, outer, 0.012) * petal(folded, wedge, radial, sharp);
}

float ringLine(float r, float target, float width) {
  return smoothstep(width, 0.0, abs(r - target));
}

/** \`count\` radial spokes. |cos| has twice as many lobes, hence the halved count. */
float spokes(float angle, float count, float sharp) {
  return pow(abs(cos(angle * count * 0.5)), sharp);
}

vec3 ramp(float t) {
  t = clamp(t, 0.0, 1.0);
  return t < 0.5
    ? mix(uCoreColor, uMidColor, t * 2.0)
    : mix(uMidColor, uEdgeColor, (t - 0.5) * 2.0);
}

void main() {
  float radius = length(vLocal);
  float r = radius / uRadius;
  if (r > 1.0) discard;

  float angle = atan(vLocal.y, vLocal.x);

  // Breathing scales the figure very slightly. Applied to the radius rather than
  // to the transform so the geometry and its bounding sphere stay untouched.
  float breathe = 1.0 - uBreath * 0.018;
  r *= breathe;

  float figure = 0.0;

  // Four concentric bands. Symmetry rises outward and the spin alternates
  // direction, which is what makes the whole thing read as a mandala turning
  // rather than as one texture spinning.
  figure += petalBand(r, angle,  6.0, 0.085, 0.290,  0.075, 1.30) * 1.00;
  figure += petalBand(r, angle, 12.0, 0.275, 0.505, -0.052, 1.60) * 0.85;
  figure += petalBand(r, angle, 24.0, 0.490, 0.730,  0.036, 2.10) * 0.62;
  figure += petalBand(r, angle, 48.0, 0.715, 0.945, -0.024, 2.80) * 0.42;

  // Ring lines delimit the bands and give the eye something crisp to hold on to
  // between the soft petals.
  float rings = 0.0;
  rings += ringLine(r, 0.085, 0.0045);
  rings += ringLine(r, 0.290, 0.0045);
  rings += ringLine(r, 0.505, 0.0035);
  rings += ringLine(r, 0.730, 0.0035);
  rings += ringLine(r, 0.945, 0.0028);
  figure += rings * 0.55;

  // Slowly counter-rotating spokes tie the bands together.
  figure += spokes(angle - uTime * 0.021, 24.0, 26.0) * bandMask(r, 0.30, 0.95, 0.06) * 0.20;
  figure += spokes(angle + uTime * 0.045, 6.0, 12.0) * bandMask(r, 0.05, 0.32, 0.04) * 0.16;

  // The lit centre.
  figure += exp(-r * r * 26.0) * 1.35;

  // A dilation travelling outward from the middle. This is the single strongest
  // cue that the figure is alive rather than merely rotating, and it is also the
  // hook the real runtime will drive from the beat grid.
  float scan = 0.5 + 0.5 * sin(r * 24.0 - uTime * 0.85);
  figure *= 0.82 + 0.18 * scan;
  figure *= 1.0 + uPulse * 0.32;

  // Fade the outer edge so the disc has no visible rim.
  figure *= 1.0 - smoothstep(0.90, 1.0, r);

  vec3 color = ramp(r * 1.04) * figure;
  gl_FragColor = vec4(color * uGain * (0.88 + 0.12 * uBreath), 1.0);
}
`;

export class MandalaCore {
  readonly object: Mesh;
  private readonly material: ShaderMaterial;
  private readonly geometry: CircleGeometry;

  constructor(options: MandalaCoreOptions = {}) {
    const radius = options.radius ?? 9;

    // 96 segments is enough that the silhouette is round; all interior detail is
    // analytic, so more vertices would buy nothing.
    this.geometry = new CircleGeometry(radius, 96);

    this.material = new ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uRadius: { value: radius },
        // The mandala fills a third of the frame, so its *average* brightness
        // matters more than its peak. At the original 1.15 the whole disc read as
        // a light source and desaturated toward white; at 0.38 the centre still
        // clips gently while the bands keep their cyan.
        uGain: { value: options.gain ?? 0.38 },
        uBreath: { value: 0 },
        uPulse: { value: 0 },
        uCoreColor: { value: new Color().copy(Palette.mandalaCore) },
        uMidColor: { value: new Color().copy(Palette.mandalaMid) },
        uEdgeColor: { value: new Color().copy(Palette.mandalaEdge) },
      },
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
      fog: false,
    });

    this.object = new Mesh(this.geometry, this.material);
    this.object.renderOrder = 10;
  }

  update(time: number, breath: number, pulse: number): void {
    const uniforms = this.material.uniforms;
    uniforms['uTime']!.value = time;
    uniforms['uBreath']!.value = breath;
    uniforms['uPulse']!.value = pulse;
    // A whole-figure drift far slower than any internal band, so the composition
    // keeps reorganising over minutes.
    this.object.rotation.z = time * 0.0125;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}
